import { zodResolver } from '@hookform/resolvers/zod';
import { CustomerInputSchema, type CustomerInput } from '@rfleet/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MapPin, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Can } from '../../app/guards';
import { Badge, Dialog, EmptyState, Field, PageHeader, Pagination, Skeleton, Spinner, Table, Td, Th, useConfirm, useToast } from '../../components/ui';
import { api, qs, type Paginated } from '../../lib/api';

export interface Customer {
  id: string;
  name: string;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  taxId: string | null;
  address: string | null;
  notes: string | null;
  isActive: boolean;
  _count: { places: number; trips: number; invoices: number };
}

export function CustomersPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const confirmDialog = useConfirm();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Customer | 'new' | null>(null);
  const q = useQuery({ queryKey: ['customers', page, search], queryFn: () => api<Paginated<Customer>>(`/customers${qs({ page, q: search })}`) });
  const remove = useMutation({ mutationFn: (id: string) => api(`/customers/${id}`, { method: 'DELETE' }), onSuccess: () => { toast.success(t('common.saved')); void qc.invalidateQueries({ queryKey: ['customers'] }); }, onError: toast.error });

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t('customers.title')}
        subtitle={q.data ? t('common.totalCount', { count: q.data.meta.total }) : undefined}
        actions={<div className="flex gap-2"><Link to="/places" className="btn-secondary"><MapPin size={16} /> {t('places.title')}</Link><Can perm="customers:create"><button className="btn-primary" onClick={() => setEditing('new')}><Plus size={16} /> {t('customers.add')}</button></Can></div>}
      />
      <input className="input max-w-xs" placeholder={t('common.search')} value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} aria-label={t('common.search')} />
      {q.isLoading ? <Skeleton className="h-72" /> : !q.data?.data.length ? <div className="card"><EmptyState title={t('customers.empty')} /></div> : (
        <Table>
          <thead><tr><Th>{t('customers.name')}</Th><Th>{t('customers.contact')}</Th><Th>{t('customers.taxId')}</Th><Th>{t('places.title')}</Th><Th>{t('nav.trips')}</Th><Th>{t('settings.users.status')}</Th><Th className="w-1" sticky="end" /></tr></thead>
          <tbody>
            {q.data.data.map((c) => (
              <tr key={c.id} className="group hover:bg-surface-hover">
                <Td className="font-semibold">{c.name}{c.address ? <div className="text-xs font-normal text-text-muted">{c.address}</div> : null}</Td>
                <Td>{c.contactName ?? '—'}{c.phone ? <div className="text-xs text-text-muted" dir="ltr">{c.phone}</div> : null}</Td>
                <Td><span dir="ltr">{c.taxId ?? '—'}</span></Td>
                <Td>{c._count.places}</Td>
                <Td>{c._count.trips}</Td>
                <Td><Badge tone={c.isActive ? 'success' : 'neutral'} dot>{c.isActive ? t('customers.active') : t('customers.inactive')}</Badge></Td>
                <Td sticky="end"><div className="flex justify-end gap-1"><Can perm="customers:update"><button className="btn-ghost size-9 !p-0" aria-label={t('common.edit')} onClick={() => setEditing(c)}><Pencil size={16} /></button></Can><Can perm="customers:delete"><button className="btn-ghost size-9 !p-0 text-danger-fg" aria-label={t('common.delete')} onClick={() => confirmDialog(t('customers.deleteConfirm', { name: c.name }), { danger: true }).then((ok) => ok && remove.mutate(c.id))}><Trash2 size={16} /></button></Can></div></Td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr><td colSpan={7}><Pagination page={q.data.meta.page} pageSize={q.data.meta.pageSize} total={q.data.meta.total} onPage={setPage} /></td></tr></tfoot>
        </Table>
      )}
      {editing ? <CustomerDialog customer={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}

function CustomerDialog({ customer, onClose }: { customer: Customer | null; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const form = useForm<CustomerInput>({ resolver: zodResolver(CustomerInputSchema), defaultValues: customer ? { name: customer.name, contactName: customer.contactName ?? '', phone: customer.phone ?? '', email: customer.email ?? '', taxId: customer.taxId ?? '', address: customer.address ?? '', notes: customer.notes ?? '', isActive: customer.isActive } : { isActive: true } });
  const save = useMutation({
    mutationFn: (v: CustomerInput) => (customer ? api(`/customers/${customer.id}`, { method: 'PATCH', json: v }) : api('/customers', { method: 'POST', json: v })),
    onSuccess: () => { toast.success(t('common.saved')); void qc.invalidateQueries({ queryKey: ['customers'] }); onClose(); },
    onError: toast.error,
  });
  const e = form.formState.errors;
  return (
    <Dialog open onClose={onClose} title={customer ? t('common.edit') : t('customers.add')} size="lg" footer={<><button className="btn-secondary" onClick={onClose}>{t('common.cancel')}</button><button className="btn-primary" form="customer-form" type="submit" disabled={save.isPending}>{save.isPending ? <Spinner /> : null}{t('common.save')}</button></>}>
      <form id="customer-form" onSubmit={form.handleSubmit((v) => save.mutate(v))} className="grid gap-4 sm:grid-cols-2" noValidate>
        <Field label={t('customers.name')} required error={e.name?.message}>{(id) => <input id={id} className="input" {...form.register('name')} />}</Field>
        <Field label={t('customers.contact')}>{(id) => <input id={id} className="input" {...form.register('contactName')} />}</Field>
        <Field label={t('drivers.phone')} error={e.phone?.message}>{(id) => <input id={id} dir="ltr" className="input" {...form.register('phone')} />}</Field>
        <Field label={t('login.email')} error={e.email?.message}>{(id) => <input id={id} type="email" dir="ltr" className="input" {...form.register('email')} />}</Field>
        <Field label={t('customers.taxId')}>{(id) => <input id={id} dir="ltr" className="input" {...form.register('taxId')} />}</Field>
        <Field label={t('customers.address')}>{(id) => <input id={id} className="input" {...form.register('address')} />}</Field>
        <div className="sm:col-span-2"><Field label={t('vehicles.notes')}>{(id) => <textarea id={id} className="input min-h-20" {...form.register('notes')} />}</Field></div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="accent-brand" {...form.register('isActive')} /> {t('customers.active')}</label>
      </form>
    </Dialog>
  );
}
